import I18n from 'i18n-js';
import { AppLanguages } from '../assets/languages';
import en from './locales/en';
import es from './locales/es';
import fr from './locales/fr';
import ar from './locales/ar';
// Per-surface namespaces (filled incrementally / by parallel extraction).
import authEn from './locales/auth/en';
import authEs from './locales/auth/es';
import authFr from './locales/auth/fr';
import authAr from './locales/auth/ar';
import dashEn from './locales/dashboard/en';
import dashEs from './locales/dashboard/es';
import dashFr from './locales/dashboard/fr';
import dashAr from './locales/dashboard/ar';
import mycardsEn from './locales/mycards/en';
import mycardsEs from './locales/mycards/es';
import mycardsFr from './locales/mycards/fr';
import mycardsAr from './locales/mycards/ar';
import settingsEn from './locales/settings/en';
import settingsEs from './locales/settings/es';
import settingsFr from './locales/settings/fr';
import settingsAr from './locales/settings/ar';
import billingEn from './locales/billing/en';
import billingEs from './locales/billing/es';
import billingFr from './locales/billing/fr';
import billingAr from './locales/billing/ar';
import editorEn from './locales/editor/en';
import editorEs from './locales/editor/es';
import editorFr from './locales/editor/fr';
import editorAr from './locales/editor/ar';
import videocardsEn from './locales/videocards/en';
import videocardsEs from './locales/videocards/es';
import videocardsFr from './locales/videocards/fr';
import videocardsAr from './locales/videocards/ar';
import scheduleEn from './locales/schedule/en';
import scheduleEs from './locales/schedule/es';
import scheduleFr from './locales/schedule/fr';
import scheduleAr from './locales/schedule/ar';
import engagementEn from './locales/engagement/en';
import engagementEs from './locales/engagement/es';
import engagementFr from './locales/engagement/fr';
import engagementAr from './locales/engagement/ar';
import rsvpEn from './locales/rsvp/en';
import rsvpEs from './locales/rsvp/es';
import rsvpFr from './locales/rsvp/fr';
import rsvpAr from './locales/rsvp/ar';
// Translations for the old flat keys still referenced by legacy i18n.t() calls.
import legacyEs from './locales/legacy/es';
import legacyFr from './locales/legacy/fr';
import legacyAr from './locales/legacy/ar';

export type LocaleCode = 'en' | 'es' | 'fr' | 'ar';
export type Dir = 'ltr' | 'rtl';

export interface LanguageMeta {
	code: LocaleCode;
	/** English name */
	label: string;
	/** Name in its own language, for the switcher */
	nativeLabel: string;
	dir: Dir;
}

export const LANGUAGES: LanguageMeta[] = [
	{ code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
	{ code: 'es', label: 'Spanish', nativeLabel: 'Español', dir: 'ltr' },
	{ code: 'fr', label: 'French', nativeLabel: 'Français', dir: 'ltr' },
	{ code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl' },
];

const RTL_LOCALES: LocaleCode[] = ['ar'];
export const STORAGE_KEY = 'cc_locale';

// Merge the legacy flat `en` keys (used by existing I18n.t calls) with the new
// nested dictionaries so nothing that already uses i18n-js breaks.
I18n.fallbacks = true;
I18n.defaultLocale = 'en';
I18n.translations = {
	en: { ...((AppLanguages && (AppLanguages as any).en) || {}), ...en, ...authEn, ...dashEn, ...mycardsEn, ...settingsEn, ...billingEn, ...editorEn, ...videocardsEn, ...scheduleEn, ...engagementEn, ...rsvpEn },
	es: { ...legacyEs, ...es, ...authEs, ...dashEs, ...mycardsEs, ...settingsEs, ...billingEs, ...editorEs, ...videocardsEs, ...scheduleEs, ...engagementEs, ...rsvpEs },
	fr: { ...legacyFr, ...fr, ...authFr, ...dashFr, ...mycardsFr, ...settingsFr, ...billingFr, ...editorFr, ...videocardsFr, ...scheduleFr, ...engagementFr, ...rsvpFr },
	ar: { ...legacyAr, ...ar, ...authAr, ...dashAr, ...mycardsAr, ...settingsAr, ...billingAr, ...editorAr, ...videocardsAr, ...scheduleAr, ...engagementAr, ...rsvpAr },
} as any;

export function isSupported(code: string): code is LocaleCode {
	return LANGUAGES.some((l) => l.code === code);
}

export function dirFor(code: LocaleCode): Dir {
	return RTL_LOCALES.includes(code) ? 'rtl' : 'ltr';
}

/** Saved preference → browser language → English. */
export function detectInitialLocale(): LocaleCode {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved && isSupported(saved)) return saved;
	} catch {
		/* localStorage unavailable */
	}
	const nav = (typeof navigator !== 'undefined' ? navigator.language : 'en') || 'en';
	const short = nav.slice(0, 2).toLowerCase();
	return isSupported(short) ? short : 'en';
}

export default I18n;
